/**
 * useCurrentPageContext Hook
 *
 * Returns the current page URL and title for sharing/messaging purposes.
 * Used by the CommandPalette quick message feature.
 */
import { useMemo } from 'react';
import { useLocation } from '@/hooks/useLocation';
import { useTabsContext } from '@/context/TabsContext';

export type PageContext = {
  /** Full page URL (origin + pathname) */
  url: string;
  /** Page title from tab or document */
  title: string;
};

/**
 * Hook to get current page context for messaging
 */
export function useCurrentPageContext(): PageContext {
  const location = useLocation();
  const { activeTab } = useTabsContext();

  return useMemo(() => {
    // Build full URL from origin and pathname
    const url = `${window.location.origin}${location.pathname}${location.search}`;
    
    // Use tab title if available, fall back to document title, then pathname
    const title = activeTab?.title || document.title || location.pathname;

    return { url, title };
  }, [location.pathname, location.search, activeTab?.title]);
}

export default useCurrentPageContext;
